// @flow
import React from 'react';
import { Row, Col, Form, Button } from 'react-bootstrap';
import { withRouter } from 'react-router';
import { Elements } from 'react-stripe-elements';
import { connect } from 'react-redux';
import Api from '../../../api/api';
import Validator from '../../Validation/Validator';
import type { UserType, CreditCardType, cardValidationStatusType, ErrorType } from '../../../utils/types';
import CreditCardForm from './CreditCardForm';
import PersonalDataForm from './PersonalDataForm';
import ChangePasswordModal from './ChangePasswordModal';
import SubscriptionModal from './SubscriptionModal';
import SubscriptionButtons from './SubscriptionButtons';
import SaveCancelButtons from './SaveCancelButtons';
import MobileAppModal from './MobileAppModal';

type Props = {
  user: UserType,
  card: CreditCardType,
  paymentError: boolean,
  showSuspendSubscriptionModal: boolean,
  showMobileAppModal: boolean,
  closeSuspendSubscriptionModal: () => void,
  closeMobileAppModal: () => void,
  setMessage: (text: string, type?: string) => void,
  fetchUserData: () => void,
  fetchCreditCardData: () => void,
  fetchExercises: () => void,
  fetchSchedule: () => void,
};

type State = {
  user: UserType,
  card: CreditCardType,
  newCardValidationStatus: cardValidationStatusType,
  errors: Array<ErrorType>,
  personalDataEditedFields: Array<string>,
  creditCardWasEdited: boolean,
  creditCardErrorFields: Array<string>,
  editMode: boolean,
  showChangePasswordModal: boolean,
  showUnsubscribeModal: boolean,
  showResubscribeModal: boolean,
  showSuspendSubscriptionModal: boolean,
  showMobileAppModal: boolean,
};

class Profile extends React.Component<Props, State> {
  state = {
    user: this.props.user,
    card: this.props.card,
    newCardValidationStatus: {
      number: false,
      expiration_date: false,
      cvc: false,
      zipcode: false,
    },
    errors: [],
    personalDataEditedFields: [],
    creditCardWasEdited: false,
    creditCardErrorFields: [],
    editMode: false,
    showChangePasswordModal: false,
    showUnsubscribeModal: false,
    showResubscribeModal: false,
    showSuspendSubscriptionModal: this.props.showSuspendSubscriptionModal,
    showMobileAppModal: this.props.showMobileAppModal,
  };

  validator = new Validator({
    first_name: [Validator.required, Validator.noForbiddenSigns],
    last_name: [Validator.required, Validator.noForbiddenSigns],
    email: [Validator.required, Validator.email],
  });

  componentWillReceiveProps(nextProps: Props): void {
    this.setState({
      user: nextProps.user,
      card: nextProps.card,
      showSuspendSubscriptionModal: nextProps.showSuspendSubscriptionModal,
      showMobileAppModal: nextProps.showMobileAppModal,
    });
  }

  handlePersonalDataChange = (field: string, e: { target: HTMLInputElement }): void => {
    if (this.state.personalDataEditedFields.indexOf(field) < 0) {
      this.setState({ personalDataEditedFields: [...this.state.personalDataEditedFields, field] });
    }

    this.setState({
      editMode: true,
      user: {
        ...this.state.user,
        [field]: e.target.value,
      },
    });
  };

  handleCreditCardDataChange = (e: SyntheticInputEvent<HTMLInputElement>, field: string): void => {
    // $FlowFixMe
    if (!e.empty) {
      !this.state.creditCardWasEdited && this.setState({ creditCardWasEdited: true });
      !this.state.editMode && this.setState({ editMode: true });
    }

    // $FlowFixMe
    if (e.error && !this.state.creditCardErrorFields.includes(field)) {
      this.setState({
        creditCardErrorFields: [...this.state.creditCardErrorFields, field],
        newCardValidationStatus: { ...this.state.newCardValidationStatus, [field]: false },
      });
    }

    // $FlowFixMe
    if (e.complete) {
      const errorFields = this.state.creditCardErrorFields.slice();
      const index = errorFields.findIndex(error => error === field);

      index >= 0 && errorFields.splice(index, 1);

      this.setState({
        creditCardErrorFields: errorFields,
        newCardValidationStatus: { ...this.state.newCardValidationStatus, [field]: true },
      });
    }
  };

  clearCreditCardFields = () => {
    //$FlowFixMe
    this.creditCardForm.cardNumberInput.clear();
    //$FlowFixMe
    this.creditCardForm.expiryDateInput.clear();
    //$FlowFixMe
    this.creditCardForm.cvcInput.clear();
    //$FlowFixMe
    this.creditCardForm.zipcodeInput.clear();
  };

  cancelChanges = (): void => {
    this.setState({
      user: this.props.user,
      card: this.props.card,
      errors: [],
      editMode: false,
      creditCardWasEdited: false,
      personalDataEditedFields: [],
    });

    this.clearCreditCardFields();
    this.cancelCreditCardErrorFieldsHighlight(false);
  };

  prepareDataForValidation = (changedFieldArray: Array<string>): Object => {
    const objectForValidation = {};

    for (let i = 0, len = changedFieldArray.length, key = ''; i < len; i++) {
      key = changedFieldArray[i];
      objectForValidation[key] = this.state.user[key];
    }

    return objectForValidation;
  };

  createPersonalDataValidator = (): Object => {
    const newValidator = new Validator({});
    const rules = this.validator.rules;
    const personalDataEditedFields = this.state.personalDataEditedFields;

    for (let field in rules) {
      if (rules.hasOwnProperty(field) && personalDataEditedFields.indexOf(field) >= 0) {
        newValidator.rules[field] = rules[field];
      }
    }

    return newValidator;
  };

  checkEmail = (): void => Api.users.checkEmail(this.state.user.email);

  handleModalShow = (modal: string): void => this.setState({ [modal]: true });

  handleModalHide = (modal: string): void => this.setState({ [modal]: false });

  closeSuspendSubscriptionModal = () => {
    this.setState({ showSuspendSubscriptionModal: false });
    this.props.closeSuspendSubscriptionModal();
  };

  closeMobileAppModal = () => {
    this.setState({ showMobileAppModal: false });
    this.props.closeMobileAppModal();
  };

  highlightCreditCardErrorFields = () => {
    const fields = document.querySelectorAll('.StripeElement--empty');

    if (fields.length) {
      for (let i = 0; i < fields.length; i++) {
        fields[i].classList.add('error');
      }
    }
  };

  cancelCreditCardErrorFieldsHighlight = (complete = true) => {
    const fields = document.querySelectorAll(`.error.StripeElement${complete ? '--complete' : ''}`);

    if (fields.length) {
      for (let i = 0; i < fields.length; i++) {
        fields[i].classList.remove('error');
      }
    }
  };

  handleSubmit = (): void => {
    this.setState({ errors: [] }, async (): Promise<void> => {
      let errors = [];
      const {
        personalDataEditedFields,
        creditCardWasEdited,
        user,
        newCardValidationStatus,
        creditCardErrorFields,
      } = this.state;
      const editedPersonalData = this.prepareDataForValidation(personalDataEditedFields);
      const creditCardValidationError =
        creditCardErrorFields.length || Object.values(newCardValidationStatus).filter(value => !value).length;

      if (personalDataEditedFields.length) {
        const personalDataValidator = this.createPersonalDataValidator();

        errors = personalDataValidator.validate(editedPersonalData);

        if (user.email !== this.props.user.email && (await this.checkEmail())) {
          errors.push({ field: 'email', message: 'Email is already in use' });
        }
      }

      if (errors.length) {
        this.setState({ errors });
      } else {
        if (creditCardWasEdited && creditCardValidationError) {
          this.highlightCreditCardErrorFields();

          return;
        }

        if (personalDataEditedFields.length) {
          Api.users
            .updateUserData({ user: editedPersonalData })
            .then((): void => {
              this.props.fetchUserData();
              this.setState({ user: this.props.user });
              this.props.setMessage('Account Information section successfully updated.');
            })
            .catch((): void => this.props.setMessage('Changes were not saved', 'danger'));
        }

        if (creditCardWasEdited && !creditCardValidationError) {
          //$FlowFixMe
          this.creditCardForm
            .createStripeToken({ type: 'card', name: `${user.first_name} ${user.last_name}` })
            .then(response => {
              Api.users
                .updateCardData({ card: { stripe_token: response.token.id } })
                .then((): void => {
                  this.props.fetchCreditCardData();
                  this.setState({ card: this.props.card });
                  this.clearCreditCardFields();
                  this.props.setMessage('Account Information section successfully updated.');
                })
                .catch((): void => this.props.setMessage('Changes were not saved', 'danger'));
            });
        }

        this.setState({ editMode: false, creditCardWasEdited: false, personalDataEditedFields: [] });
      }
    });
  };

  handleResubscribe = (): void =>
    Api.users
      .subscribe()
      .then((): void => {
        this.props.fetchUserData();
        this.props.fetchExercises();
        this.props.fetchSchedule();
        this.props.setMessage(
          "Congratulations! Your subscription has been successfully resumed. We're so glad you're participating with fellow believers in drawing near to God (James 4:8).",
        );
      })
      .catch((error): void => {
        const message = Object.values(error.response.data)[0];
        message && this.props.setMessage(String(message), 'danger');
      });

  handleUnsubscribe = (): void =>
    Api.users
      .unsubscribe()
      .then((): void => {
        this.setState({ showUnsubscribeModal: false });
        this.props.setMessage(
          "Your subscription has been terminated. May the God who promises to draw near to us be your guide throughout your days, every day, so that all of your life, even the most ordinary parts, becomes extraordinary because it's infused with His presence.",
          'info',
        );
        this.props.fetchUserData();
      })
      .catch((): void => this.props.setMessage('Server error.'));

  handleSuspendSubscription = (): void =>
    Api.users
      .suspendSubscription()
      .then((): void => {
        this.props.closeSuspendSubscriptionModal();
        this.props.setMessage(
          'Your subscription is now paused. After 30 days it will be automatically resumed.',
          'info',
        );
        this.props.fetchUserData();
      })
      .catch((error): void => {
        this.props.closeSuspendSubscriptionModal();
        const message = Object.values(error.response.data)[0];
        message && this.props.setMessage(String(message), 'danger');
      });

  render() {
    const { paymentError, setMessage } = this.props;
    const {
      user,
      card,
      creditCardWasEdited,
      errors,
      editMode,
      showChangePasswordModal,
      showUnsubscribeModal,
      showSuspendSubscriptionModal,
      showResubscribeModal,
      showMobileAppModal,
    } = this.state;

    return (
      <div className={`main-profile-grid ${user.subscribed && !paymentError ? 'subscribed' : 'unsubscribed'}`}>
        <Row className="show-grid display-flex flex-direction-profile">
          <Col md={4} sm={12} xs={12}>
            <Form onSubmit={this.handleSubmit} autoComplete="off">
              {Object.values(user).length && (
                <Col md={12}>
                  <PersonalDataForm
                    user={user}
                    errors={errors}
                    handlePersonalDataChange={this.handlePersonalDataChange}
                  />
                  <div className="profile-info-buttons ch-pass ch-pass-first">
                    <Button onClick={() => this.handleModalShow('showChangePasswordModal')} bsStyle="primary">
                      Change password
                    </Button>
                  </div>
                </Col>
              )}
            </Form>
          </Col>

          <Col md={8} sm={12} xs={12} className="for-ie-11-div">
            <Col md={12} className="for-ie-11-reverse">
              <div className="profile-reverse">
                <Col md={6} className="save-cancel-first resubscribe-button">
                  {(!user.subscribed || paymentError) && (
                    <span className="currently-unsubscribed">You are currently unsubscribed</span>
                  )}
                  <SubscriptionButtons
                    subscribed={user.subscribed}
                    showPauseSubscriptionButton={user.payment_system === 'stripe'}
                    paymentError={paymentError}
                    handleModalShow={this.handleModalShow}
                    handleResubscribe={
                      user.payment_system === 'apple'
                        ? () => this.handleModalShow('showResubscribeModal')
                        : this.handleResubscribe
                    }
                  />
                </Col>
                <Col md={6} className="save-cancel">
                  <SaveCancelButtons
                    disabled={!editMode}
                    cancelChanges={this.cancelChanges}
                    saveChanges={this.handleSubmit}
                  />
                </Col>
              </div>
            </Col>

            {user.payment_system === 'stripe' && (
              <Form className="for-ie-11-form" onSubmit={this.handleSubmit} autoComplete="off">
                <Col md={6}>
                  <Elements>
                    <CreditCardForm
                      //$FlowFixMe
                      onRef={ref => (this.creditCardForm = ref)}
                      card={card}
                      creditCardWasEdited={creditCardWasEdited}
                      handleCreditCardDataChange={this.handleCreditCardDataChange}
                      cancelCreditCardErrorFieldsHighlight={this.cancelCreditCardErrorFieldsHighlight}
                    />
                  </Elements>
                </Col>
                <Col md={6} />
              </Form>
            )}
          </Col>
        </Row>

        <MobileAppModal show={showMobileAppModal} onHide={this.closeMobileAppModal} />

        <ChangePasswordModal
          show={showChangePasswordModal}
          setMessage={setMessage}
          hideModal={() => this.handleModalHide('showChangePasswordModal')}
        />

        <SubscriptionModal
          show={showUnsubscribeModal}
          onHide={() => this.handleModalHide('showUnsubscribeModal')}
          handleSubscriptionAction={this.handleUnsubscribe}
          hideButtons={user.payment_system === 'apple'}
        >
          {user.payment_system === 'apple' ? (
            <h4 className="modal-title in-bottom-title">Unsubscribe through mobile</h4>
          ) : (
            <React.Fragment>
              <p className="first-diamond">
                We hope these exercises helped you enjoy God in purposeful pursuit. It was our honor to help in this
                process.
              </p>

              <p className="diamond">
                If you need to terminate your subscription, you will:
                <ul>
                  <li>no longer receive new emails with exercises</li>
                  <li>not be able to load content from links</li>
                </ul>
              </p>
              <h4 className="modal-title in-bottom-title">Are you sure you want to unsubscribe?</h4>
            </React.Fragment>
          )}
        </SubscriptionModal>

        <SubscriptionModal
          show={showSuspendSubscriptionModal}
          onHide={this.closeSuspendSubscriptionModal}
          handleSubscriptionAction={this.handleSuspendSubscription}
        >
          <p className="first-diamond">
            Your service will be paused for 30 days. During this period, we hope you do not pause from practicing God's
            presence. A simple flash prayer, "I love you, Lord," or 10 minutes of daily rest can change the day for you
            and others.
          </p>

          <p className="diamond">
            During this period, we will:
            <ul>
              <li>not charge your account</li>
              <li>not send new emails with exercises</li>
              <li>not enable email or website links to load exercise content</li>
              <li>miss you terribly and hope that you come back</li>
            </ul>
          </p>
          <h4 className="modal-title in-bottom-title">Are you sure you want to pause your subscription?</h4>
        </SubscriptionModal>

        <SubscriptionModal
          show={showResubscribeModal}
          onHide={() => this.handleModalHide('showResubscribeModal')}
          cancelButtonText="OK"
          hideButtons={user.payment_system === 'apple'}
        >
          {user.payment_system === 'apple' ? (
            <h4 className="modal-title in-bottom-title">Unsubscribe through mobile</h4>
          ) : (
            <h4 className="modal-title in-bottom-title">
              The process of resuming your subscription is in progress. It may take up to several hours.
            </h4>
          )}
        </SubscriptionModal>
      </div>
    );
  }
}

const mapStateToProps = state => ({ user: state.user, card: state.card });

export default withRouter(connect(mapStateToProps)(Profile));



// WEBPACK FOOTER //
// ./src/components/Dashboard/Profile/Profile.js